import {ChangeDetectionStrategy, Component, DOCUMENT, ElementRef, Inject, Input, OnInit} from '@angular/core';
import {AsyncSubject, BehaviorSubject, from} from 'rxjs';
import {filter, switchMap, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../../../components/destroyable';
import {Widget} from '../../../components/layout/widget/widget';
import {IChartingLibraryWidget} from '../../../components/trading-view-chart/charting-library';
import {Security} from '../../../models/security';
import {SecurityKey} from '../../../models/security-key';
import {ChartWidget} from '../chart-widget';

@Component({
    selector: 'chart-security-input',
    templateUrl: './chart-security-input.component.html',
    styleUrls: ['./chart-security-input.injection.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChartSecurityInputComponent implements OnInit {

    /**
     * The string, which is used to find <style>.
     * That string should be included at the beginning of the styles.
     * The content from that tag then should be injected to the chart document.
     */
    private static STYLES_SEARCH_MAGIC_STRING: string = '5e3E5gLW';

    @Input()
    public chartContainer: HTMLElement;

    @Input()
    public chartReady$: AsyncSubject<boolean>;

    @Input()
    public chartInstance$: BehaviorSubject<IChartingLibraryWidget>;

    public securityKey: SecurityKey;

    public readonly destroy$ = destroy$();

    public chartIFrame: HTMLIFrameElement;

    public chartDocument: Document;

    constructor(
        @Inject(Widget)
        private widget: ChartWidget,
        private elementRef: ElementRef,
        @Inject(DOCUMENT)
        private document: Document
    ) {}

    // Ref.: https://juristr.com/blog/2019/08/dynamically-load-css-angular-cli/
    private static injectStyles(document: Document, styles: string): void {
        const headElement: HTMLHeadElement = document.head || document.getElementsByTagName('head')[0];
        const styleElement: HTMLStyleElement = document.createElement('style');

        headElement.appendChild(styleElement);
        styleElement.appendChild(document.createTextNode(styles));
    }

    /**
     * Searches the <style> tag, which contains styles, containing the "magic string" at the beginning.
     */
    private static findStylesTagByMagicString(document: Document): HTMLStyleElement | undefined {
        const headElement: HTMLHeadElement = document.head || document.getElementsByTagName('head')[0];
        const styleTags = headElement.querySelectorAll('style');
        let foundStyleTag: HTMLStyleElement;

        for (let iStyleTag = 0; iStyleTag < styleTags.length; iStyleTag++) {
            const styleTag = styleTags[iStyleTag];

            const isFound = styleTag.textContent
                .substring(0, 255) // Should speed up the search.
                .includes(this.STYLES_SEARCH_MAGIC_STRING);

            if (isFound) {
                foundStyleTag = styleTag;
                break;
            }
        }

        return foundStyleTag;
    }

    public ngOnInit(): void {
        this.chartInstance$.pipe(
            filter(chartInstance => !!chartInstance),
            switchMap(chartInstance => from(chartInstance.headerReady())),
            tap(() => this.initReferences()),
            tap(() => this.replaceChartNativeSymbolInput()),
            takeUntil(this.destroy$)
        ).subscribe();

        this.listenToWidgetSettings();
    }

    public onSecurityChange(security: Security): void {
        this.widget.implementationInstance.updateSettings({
            securityKey: security
        }).subscribe();
    }

    private initReferences(): void {
        this.chartIFrame = this.chartContainer.querySelector('iframe');
        this.chartDocument = this.chartIFrame.contentDocument || this.chartIFrame.contentWindow.document;

        this.destroy$.subscribe(() => {
            this.chartIFrame = null;
            this.chartDocument = null;
        });
    }

    private replaceChartNativeSymbolInput(): void {
        const NATIVE_SYMBOL_INPUT_SELECTOR = '#header-toolbar-symbol-search';

        const nativeControl: HTMLElement = this.chartDocument.querySelector(NATIVE_SYMBOL_INPUT_SELECTOR);
        const newControl: HTMLElement = this.elementRef.nativeElement;

        if (nativeControl) {
            const styleTag = ChartSecurityInputComponent.findStylesTagByMagicString(this.document);

            if (styleTag) {
                const chartInjectionStyles = styleTag.textContent;

                ChartSecurityInputComponent.injectStyles(this.chartDocument, chartInjectionStyles);

                nativeControl.parentNode.insertBefore(newControl, nativeControl);
            } else {
                console.warn('[ChartSecurityInputComponent] Cannot find styles tag.');
            }
        } else {
            console.warn('[ChartSecurityInputComponent] Cannot find symbol input in the chart.');
        }
    }

    private listenToWidgetSettings(): void {
        this.widget.implementationInstance.getSettingsSecurityKeyStream().pipe(
            tap(securityKey => this.securityKey = securityKey),
            takeUntil(this.destroy$)
        ).subscribe();
    }

}
